import get from 'lodash/get'
import map from 'lodash/map'
import compact from 'lodash/compact'
import castArray from 'lodash/castArray'
import { List } from 'immutable'
import {
  client as apolloClient,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../apollo-graphql/index'
import { GET_NOTIFICATION_FEED, MARK_NOTIFICATIONS_RECEIVED } from './queries'
import { fromFeedConnection } from './feedAdapter'
import { TYPES } from './types'

// Re-exported so every existing `import { TYPES } from './api-notifications'` keeps working.
export { TYPES, CONTENT_TYPES } from './types'

/** One page of the `/notifications` page's list, behind its explicit "Load more" button. */
const FEED_PAGE_SIZE = 20
/** The bell dropdown's preview depth. */
const BELL_PAGE_SIZE = 10

const ERROR = { success: false }

/**
 * Does this notification count toward the bell's unread dot?
 *
 * ⚠️ Takes a notification and NOTHING else — no category, not ever. The bell's badge is an
 * unfiltered total: a member wants to know whether anything is new, not whether anything is new
 * in the tab they happen to be looking at (CO-768). Narrowing this is one of the five places the
 * tab filter could leak into the badge, which is why it is a named function with a test rather
 * than an inline predicate in `NotificationsMenu`.
 *
 * GENERAL_VIDEO_PROMOTION is excluded because it is not drawn in the bell at all —
 * `NotificationsVideoPromotion` reports its own impression where it IS drawn.
 */
export function isUnseenNotification (notification) {
  return notification.get('received') !== true
    && notification.get('type') !== TYPES.GENERAL_VIDEO_PROMOTION
}

/**
 * The member's feed language list on the wire — no English append.
 *
 * ⚠️ Appending `en` (438d958007) made Brooklyn match English-only content and message rows and
 * hydrate English titles in a German UI. Comment excerpts are translated server-side (CO-702) from
 * `languageCode` alone; they do not need `en` on the feed query. Default to English only when
 * the caller sends nothing.
 */
export function normalizeFeedLanguage (language) {
  const requested = List.isList(language) ? language.toJS() : language
  const list = compact(castArray(requested || []))
  return list.length ? list : ['en']
}

export async function getNotifications ({
  auth, language, category, after, first = FEED_PAGE_SIZE,
}) {
  try {
    const { data } = await apolloClient.query({
      query: GET_NOTIFICATION_FEED,
      // `category` is omitted when falsy, which is what the unfiltered badge read wants.
      variables: {
        category: category ? category.toUpperCase() : undefined,
        language: normalizeFeedLanguage(language),
        first,
        after,
      },
      // A poll must never be answered from cache, or the bell freezes on its first page.
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: { jwt: auth.get('jwt') },
    })
    return fromFeedConnection(get(data, 'notificationFeed'))
  } catch (e) {
    return ERROR
  }
}

const sameIdList = (a, b) => (
  a.length === b.length && a.every((id, i) => id === b[i])
)

/**
 * The bell's preview list.
 *
 * ⚠️ Takes NO `category`, and must not gain one. The dropdown DOES have Content/Community tabs
 * now, but they are fed by their own per-category requests; this list exists solely for the
 * unread badge, which is counted from it. Narrowing it would make the badge report "new in the
 * tab you last opened" instead of "new" (CO-768).
 */
export async function getRecentNotifications ({ auth, language, previousIds = [] }) {
  try {
    const { data } = await apolloClient.query({
      query: GET_NOTIFICATION_FEED,
      // ⚠️ No `category`, and it must never gain one. The unread badge is counted off this list
      // and answers "is anything new", not "is anything new in the tab you last opened"
      // (CO-768).
      variables: { language: normalizeFeedLanguage(language), first: BELL_PAGE_SIZE },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: { jwt: auth.get('jwt') },
    })
    const page = fromFeedConnection(get(data, 'notificationFeed'))

    /**
     * Steady-state short-circuit: the poll returned the same ids the store already holds.
     *
     * ⚠️ The ORIGINAL reason for this is gone. It used to skip a client-side hydration fan-out
     * (`fetchMultipleNodes` plus a member lookup each) that dominated the request's cost; the
     * server hydrates in one batched query now, so there is nothing expensive left to skip.
     *
     * It stays for the reason the old comment did not state: returning `unchanged` avoids a
     * Redux write, which avoids re-rendering the whole bell list every poll tick.
     */
    const ids = map(page.rows, (n) => get(n, 'id'))
    if (previousIds.length && sameIdList(ids, previousIds)) {
      return { unchanged: true }
    }

    return { data: page.rows }
  } catch (e) {
    return ERROR
  }
}

export async function setNotificationsReceived ({ ids, auth }) {
  try {
    const { data } = await apolloClient.mutate({
      mutation: MARK_NOTIFICATIONS_RECEIVED,
      variables: { ids: map(ids, (id) => String(id)) },
      context: { jwt: auth.get('jwt') },
    })
    /**
     * ⚠️ Idempotent, so `markedCount: 0` is SUCCESS, not failure. An id already received is
     * accepted and counted zero, and ids belonging to another member come back in `ignoredIds`
     * rather than erroring — a client retrying a failed call must not read either as a problem.
     */
    return { success: get(data, 'notificationsMarkReceived.success', false) }
  } catch (e) {
    return ERROR
  }
}
